import useSWR from 'swr';
import { useMemo } from 'react';
import { useSearchParams } from 'next/navigation';
import { URLParameters } from '@/app/common/urlParameters';
import { fetchWithTracingAndLogging } from '@/app/components/hotel-detail/common/fetchWithTracing';
import { BASE_PATH } from '@/app/components/search/utils/path';
import { getLoggingInfo } from '@/app/components/hotel-detail/lib/loggingInfo';
import { useInitialParams } from '@/app/components/hotel-detail/InitialParamsProvider';
import { useCurrentFilterStatus } from '@/app/components/hotel-detail/CurrentFilterStatusProvider';
import useSelectedTransport from '@/app/components/hotel-detail/hooks/useSelectedTransport';
import useUserConfig from '@/app/components/hotel-detail/hooks/useUserConfig';
import { useSearchSummary } from '@/app/components/hotel-detail/SearchSummary';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import { useSelectedMealPlan } from '@/app/components/hotel-detail/SelectedMealPlanProvider';
import { useOffersContext } from '@/app/components/hotel-detail/OffersProvider';
import { isMealPlanInRooms } from '@/app/components/hotel-detail/rooms/roomCommon';
import { useFetchHotelStatic } from '@/app/components/hotel-detail/hooks/useFetchHotelStatic';
import type { SearchInfoData } from '@/app/search/types.ts';
import type { CommonData } from '@/app/common/createCommonData';
import { useCreateAsyncPricing } from '../PricingProvider';
import { useUrlSearchParams } from '../UrlSearchParamsProvider';

function createTransportURLQuery({
  pricingId,
  rateId,
  page,
  debug,
  filters,
  urlAirlines,
  includePreselected = false,
}: {
  pricingId: string;
  rateId: string;
  page: number;
  debug?: string;
  filters?: TransportFiltersResponse;
  urlAirlines?: string;
  includePreselected: boolean;
}) {
  const sorting = getTravelSorting(filters?.sortOptions?.find((sort) => sort.selected)?.option);
  const filterParams = getTravelFilters(filters, urlAirlines);
  const isDebug = debug && debug === 'true' ? '&debug=true' : '';
  return `pricingId=${pricingId}${filterParams}&rateId=${rateId}&sorter-name=${sorting}&sorter-asc=true&page-size=18&page-number=${page}${isDebug}&include-preselected=${includePreselected}`;
}

export function isTravelTransportsResponseError(
  response: FullAvailableTransportResponse | TransportResponseError | undefined
): boolean {
  return (
    !response ||
    (response as TransportResponseError).errorType !== undefined ||
    (response as FullAvailableTransportResponse).availableTransports === undefined
  );
}

export function fetchTravelTransports({
  transportRequest,
  rateId,
  page,
  searchInfoData,
  commonData,
  debug,
  urlAirlines,
  includePreselected,
}: {
  transportRequest: TravelTransportRequest;
  rateId: string;
  page: number;
  searchInfoData: SearchInfoData;
  commonData: CommonData;
  debug?: string;
  urlAirlines?: string;
  includePreselected: boolean;
}) {
  const { pricingId, pageType, filters } = transportRequest;
  return fetchWithTracingAndLogging<FullAvailableTransportResponse | TransportResponseError>(
    `${BASE_PATH}/api/hotel-detail/transports?${createTransportURLQuery({ pricingId, rateId, page, debug, filters, urlAirlines, includePreselected })}`,

    {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ ...transportRequest }),
    },
    getLoggingInfo(pageType, 'travel-api-transport', 'TransportAlternativesProvider'),
    searchInfoData.searchTraceParent,
    commonData
  );
}

export default function useTransportsFetch() {
  const { pageType, priceType } = useInitialParams();
  const { searchInfoData, localization, commonData } = usePageWrapperData();
  const urlSearchParams = useUrlSearchParams();
  const { nights } = useSearchSummary({
    useRoomDates: false,
    intlDateFormat: { weekday: 'short', day: 'numeric', month: 'short' },
  });
  const { data: asyncPricing } = useCreateAsyncPricing();
  const { selectedMealPlan } = useSelectedMealPlan();
  const { offersData, isLoadingRooms } = useOffersContext();
  const { data: transportData } = useSelectedTransport();
  const selectedTransportId = transportData?.transports[0]?.id || '';
  const { currentFiltersStatus } = useCurrentFilterStatus();
  const { showBackToTravel } = useUserConfig();
  const searchParams = useSearchParams();
  const debug = searchParams.get(URLParameters.DEBUG) ?? 'false';
  const urlAirlines = normalizeAirlineParam(searchParams.get(URLParameters.AIRLINE));
  const { data: staticDataHotel } = useFetchHotelStatic();

  const guests = urlSearchParams.totalGuestsWithoutInfants;
  const transportRequest: TravelTransportRequest = {
    pricingId: asyncPricing?.pricingId || '',
    price: selectedMealPlan?.finalPrice || 0,
    businessProfileId: localization.businessProfile,
    pageType,
    priceType,
    guests,
    nights,
    filters: currentFiltersStatus,
    showBackToTravel,
    isTrain: urlSearchParams.isTrain,
    localization: { lang: localization.lang, locale: localization.locale, country: localization.country },
    hotelCoordinates: staticDataHotel?.coordinates,
  };
  const rateId = selectedMealPlan?.id ?? '';

  const swrResult = useSWR<FullAvailableTransportResponse | TransportResponseError>(
    !urlSearchParams.accommodationOnly &&
      !isLoadingRooms &&
      asyncPricing?.pricingId &&
      asyncPricing?.pricingId !== 'undefined' &&
      rateId !== '' &&
      (selectedMealPlan?.finalPrice ?? 0) > 0 &&
      nights > 0 &&
      guests > 0 &&
      selectedTransportId !== '' &&
      // When changed transport, and new rooms are loaded, it is possible that the selected meal plan is not still updated
      // with the new meal plan IDS from new rooms response
      isMealPlanInRooms(offersData?.rooms, selectedMealPlan)
      ? [
          'travel-api-transports',
          selectedTransportId,
          createTransportURLQuery({
            pricingId: asyncPricing?.pricingId,
            rateId,
            page: 1,
            debug,
            filters: currentFiltersStatus,
            urlAirlines,
            includePreselected: true,
          }),
        ]
      : null,
    async () =>
      fetchTravelTransports({
        transportRequest,
        rateId,
        page: 1,
        searchInfoData,
        commonData,
        debug,
        urlAirlines,
        includePreselected: true,
      }),
    {
      keepPreviousData: true,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      shouldRetryOnError: false,
      dedupingInterval: 1000 * 60 * 30,
    }
  );

  const preselectedTransport = offersData?.transport?.transports[0];
  const hasSettled = swrResult.data !== undefined || swrResult.error !== undefined;
  const hasFetchError =
    !!swrResult.error ||
    isTravelTransportsResponseError(swrResult.data) ||
    (swrResult.data as FullAvailableTransportResponse)?.availableTransports?.length === 0;
  const isFallbackTransport = hasSettled && hasFetchError && !!preselectedTransport;

  const fallbackResponse = useMemo<FullAvailableTransportResponse | null>(() => {
    if (!isFallbackTransport) return null;
    return {
      availableTransports: [],
      pageConfiguration: {
        currentPage: 1,
        filteredProducts: 0,
        totalPages: 1,
        totalProducts: 0,
        priceTypeSelection: {
          selected: {
            priceTypeKey: '',
            priceTypeName: (offersData?.priceType ?? 'TOTAL') as PriceType,
          },
        },
      },
      filters: {} as TransportFiltersResponse,
      formulaInfo: {},
      distances: offersData?.transport?.distances,
    };
  }, [isFallbackTransport, offersData?.priceType, offersData?.transport?.distances]);

  if (fallbackResponse) {
    return { ...swrResult, data: fallbackResponse, error: undefined, isFallbackTransport: true };
  }

  return { ...swrResult, isFallbackTransport: false };
}

function getTravelSorting(option?: SortFilterOption): TravelSortingOptions {
  return !option || option === 'POPULAR'
    ? 'TRANSPORT_RECOMMENDED'
    : option === 'FASTEST'
      ? 'TRANSPORT_FASTEST'
      : 'TRANSPORT_PRICE';
}

export function normalizeAirlineParam(value: string | null | undefined): string | undefined {
  if (!value) return undefined;
  const codes = value
    .split(/[;,]/)
    .map((code) => code.trim().toUpperCase())
    .filter((code) => /^[A-Z0-9]{2,3}$/.test(code));
  const unique = [...new Set(codes)];
  return unique.length ? unique.join(',') : undefined;
}

export function getTravelFilters(filters: TransportFiltersResponse | undefined, urlAirlines?: string) {
  const outboundTimeFilterParam = createTimeSlotFilter(filters?.outboundDepartureTimeSlots || [], 'outbound-timeslot');
  const inboundTimeFilterParam = createTimeSlotFilter(filters?.inboundDepartureTimeSlots || [], 'inbound-timeslot');
  const selectedAirlines = (filters?.airlines || filters?.companies)
    ?.filter((airline) => airline.enabled && airline.selected)
    .map((airline) => airline.code)
    .join(',');
  // First load: the filter state does not yet contain the airlines list (currentFiltersStatus = {}).
  // In that case we apply the prefilter coming from the URL (?airline=...). Once the server response
  // has arrived, the chips take over, including the "everything deselected" case.
  const hasAirlineFilterState = (filters?.airlines || filters?.companies) !== undefined;
  const airlinesFilter = hasAirlineFilterState ? selectedAirlines : urlAirlines;
  const companiesFilterParam = airlinesFilter ? `&airline=${airlinesFilter}` : '';
  const outboundStopFilterParam = createStopsFilter(filters?.outboundStops || [], 'outbound-stop');
  const inboundStopFilterParam = createStopsFilter(filters?.inboundStops || [], 'inbound-stop');

  const outboundDepAirportFilterParam = createAirportFilter(
    filters?.outboundDepartureAirports || filters?.outboundDepartureStations || [],
    'outbound-dep-airport'
  );
  const outboundArrAirportFilterParam = createAirportFilter(
    filters?.outboundArrivalAirports || filters?.outboundArrivalStations || [],
    'outbound-arr-airport'
  );
  const inboundDepAirportFilterParam = createAirportFilter(
    filters?.inboundDepartureAirports || filters?.inboundDepartureStations || [],
    'inbound-dep-airport'
  );
  const inboundArrAirportFilterParam = createAirportFilter(
    filters?.inboundArrivalAirports || filters?.inboundArrivalStations || [],
    'inbound-arr-airport'
  );

  const sameDepArrAirportFilterParam = createSameAirportFilter(
    filters?.outboundSameAirport?.on
      ? filters?.outboundDepartureAirports || []
      : filters?.outboundSameStation?.on
        ? filters?.outboundDepartureStations || []
        : [],
    'dep-arr'
  );
  const sameArrDepAirportFilterParam = createSameAirportFilter(
    filters?.inboundSameAirport?.on
      ? filters?.inboundDepartureAirports || []
      : filters?.inboundSameStation?.on
        ? filters?.inboundDepartureStations || []
        : [],
    'arr-dep'
  );

  const baggageFilterParam = createBaggageFilter(filters?.baggageIncluded || [], 'baggage-included');
  const handBaggageFilterParam = createBaggageFilter(filters?.handBaggageIncluded || [], 'hand-baggage-included');

  const directOnlyFilterParam = createDirectOnlyFilter(filters?.directOnly, 'direct-only');

  return `${outboundTimeFilterParam}${inboundTimeFilterParam}${companiesFilterParam}${outboundStopFilterParam}${inboundStopFilterParam}${outboundDepAirportFilterParam}${outboundArrAirportFilterParam}${inboundDepAirportFilterParam}${inboundArrAirportFilterParam}${sameDepArrAirportFilterParam}${sameArrDepAirportFilterParam}${baggageFilterParam}${handBaggageFilterParam}${directOnlyFilterParam}`;
}

function createTimeSlotFilter(timeSlots: TimeSlotFilter[], filterParam: FacetId) {
  const filterValue = timeSlots
    .filter((slot) => slot.enabled && slot.selected)
    .map((slot) => slot.id.toLocaleLowerCase())
    .join(',');
  return filterValue ? `&${filterParam}=${filterValue}` : '';
}

function createStopsFilter(stops: StopsFilter[], filterParam: FacetId) {
  const filterValue = stops
    .filter((stop) => stop.enabled && stop.selected)
    .map((stop) => (stop.stops === 'NO_STOPS' ? '0' : stop.stops === 'ONE' ? '1' : '2'))
    .join(',');
  return filterValue ? `&${filterParam}=${filterValue}` : '';
}

function createAirportFilter(airports: AirportFilter[], filterParam: FacetId) {
  const filterValue = airports
    .filter((airport) => airport.enabled && airport.selected)
    .map((airport) => airport.iata)
    .join(',');
  return filterValue ? `&${filterParam}=${filterValue}` : '';
}

function createSameAirportFilter(airports: AirportFilter[], filterParam: FacetId) {
  const filterValue = airports
    .filter((airport) => airport.enabled)
    .sort((a, b) => (a.iata > b.iata ? 1 : a.iata < b.iata ? -1 : 0))
    .map((airport) => airport.iata)
    .join(',');
  return filterValue ? `&${filterParam}=${filterValue}` : '';
}

function createBaggageFilter(baggageFilter: BaggageFilter[], filterParam: FacetId) {
  const filterValue = baggageFilter.some((baggage) => baggage.enabled && baggage.selected);
  return filterValue ? `&${filterParam}=${filterValue}` : '';
}

function createDirectOnlyFilter(directOnlyFilter: DirectOnlyFilter | undefined, filterParam: FacetId) {
  const filterValue = directOnlyFilter?.enabled && directOnlyFilter?.selected;
  return filterValue ? `&${filterParam}=${filterValue}` : '';
}
